import { Component, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { MessageService } from 'primeng/api';
import { combineLatest, Observable, Subject, withLatestFrom, takeUntil } from 'rxjs';
import { AddressTypeOption, ADDRESS_TYPE_OPTIONS } from 'src/app/core/constants';
import { AddressOrders, AddressType, PatientAddress } from 'src/app/core/models';
import { AddPatientAddressType } from 'src/app/core/store/addresses/addresses.actions';
import { AddressesState } from 'src/app/core/store/addresses/addresses.state';
import { FetchOrdersAndReorders } from 'src/app/core/store/order/orders.actions';
import { OrdersState } from 'src/app/core/store/order/orders.state';
import { ApiStoreData, CcsSentryError } from 'src/app/core/store/utils';

@Component({
  selector: 'app-addresses',
  templateUrl: './addresses.component.html',
  styleUrls: ['./addresses.component.scss'],
})
export class AddressesComponent implements OnInit {
  @Select(OrdersState.isLoading) isLoadingOrders$!: Observable<boolean>;
  @Select(OrdersState.error) ordersError$!: Observable<CcsSentryError | null>;
  @Select(AddressesState.patientAddressesState) patientAddressesState$!: Observable<ApiStoreData<PatientAddress[]>>;
  @Select(AddressesState.addPatientAddressTypeState) addPatientAddressTypeState$!: Observable<ApiStoreData<null>>;
  @Select(AddressesState.addressOrdersState) addressOrdersState$!: Observable<ApiStoreData<AddressOrders | null>>;

  displayModal: boolean = false;
  displayAddressOrdersModal: boolean = false;
  successMessageServiceKey: string = 'profileMessages';

  constructor(private store: Store, private messageService: MessageService) {}

  ngOnInit() {
    this.store.dispatch(new FetchOrdersAndReorders());
  }

  mapAddressTypeValuesToTagLabels(values: string[]) {
    return ADDRESS_TYPE_OPTIONS.filter((o: AddressTypeOption) => values.includes(o.value)).map((o) => o.tagLabel);
  }

  showModal() {
    this.displayModal = true;
  }

  hideModal() {
    this.displayModal = false;
  }

  showAddressOrdersModal() {
    this.displayAddressOrdersModal = true;
  }

  hideAddressOrdersModal() {
    this.displayAddressOrdersModal = false;
  }

  getButtonOptions() {
    return ADDRESS_TYPE_OPTIONS;
  }

  addAddressType(addressId: number, addressType: AddressType) {
    const terminate = new Subject<void>();

    combineLatest([
      this.store
        .dispatch(new AddPatientAddressType(addressId, addressType))
        .pipe(withLatestFrom(this.addPatientAddressTypeState$)),
      this.addressOrdersState$,
    ])
      .pipe(takeUntil(terminate))
      .subscribe(([[_, addPatientAddressTypeState], addressOrdersState]) => {
        if (addPatientAddressTypeState.error) {
          this.messageService.add({
            key: 'homeToast',
            severity: 'error',
            detail: "Could not update this address's defaults. Please try again later.",
          });
          terminate.next();
        } else if (!addressOrdersState.isLoading) {
          if (addressOrdersState.data?.orders.length) {
            this.showAddressOrdersModal();
          }
          terminate.next();
        }
      });
  }

  onAddressCreated() {
    this.messageService.clear(this.successMessageServiceKey);

    this.messageService.add({
      key: this.successMessageServiceKey,
      severity: 'success',
      detail: 'Successfully added your new address.',
    });

    const terminate = new Subject<void>();

    this.addressOrdersState$.pipe(takeUntil(terminate)).subscribe((addressOrdersState) => {
      if (!addressOrdersState.isLoading) {
        if (addressOrdersState.data?.orders.length) {
          this.showAddressOrdersModal();
        }
        terminate.next();
      }
    });
  }

  onAddressOrdersUpdated() {
    this.messageService.clear(this.successMessageServiceKey);

    this.messageService.add({
      key: this.successMessageServiceKey,
      severity: 'success',
      detail: 'Successfully updated your order addresses.',
    });
  }
}
